// per docs/shape-uc27-alert-engine.md §Implementation plan §11
// per docs/caiet-uc-spec.md §UC27-02 §UC-ALRT-05 §UC-ALRT-06
// The policy register. Editing lives at its own address (`alert-policies/new`,
// `alert-policies/:id/edit`, NotificationPolicyFormPage), as the wireframe's
// `politici/nou` and `politici/:id/edit` do (AdminAlerte.tsx:2972-3227).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import type { TableColumn, TableRowData } from '@egov-moldova/mud/loader';
import { MudButton, MudIcon, MudModal, MudInfoBox, MudTable } from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import {
  EmptyRegisterMessage,
  MetadataLine,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  RouterLink,
  RowActionsMenu,
  useToast,
} from '@simsm/ui-shared';
import { AlertsSectionNav } from '../../components/AlertsSectionNav';
import { AlertDefinitionsMenu } from '../../components/AlertDefinitionsMenu';
import type { RuleDto } from './AlertRulesPage';

export interface PolicyDto {
  id: string;
  name: string;
  displayName: string;
  matchers: Array<{ label: string; op: string; value: string | string[] }>;
  channelIds: string[];
  groupBy: string[];
  groupWaitSeconds: number;
  groupIntervalSeconds: number;
  repeatIntervalSeconds: number;
}

interface ChannelDto {
  id: string;
  name: string;
  displayName: string;
}

export const SEVERITIES = ['critical', 'warning', 'info'];

export function NotificationPoliciesPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteErrorKey, setDeleteErrorKey] = useState<string | null>(null);

  const policies = useQuery({
    queryKey: ['notification-policies'],
    queryFn: () =>
      apiClient
        .get<{ policies: PolicyDto[] }>('/admin/notification-policies')
        .then((r) => r.data.policies),
  });

  const channels = useQuery({
    queryKey: ['notification-channels'],
    queryFn: () =>
      apiClient
        .get<{ channels: ChannelDto[] }>('/admin/notification-channels')
        .then((r) => r.data.channels),
  });

  // Which policies a rule still routes through — the register disables the delete
  // and says why, and the gateway refuses it (there is no foreign key either way).
  const rules = useQuery({
    queryKey: ['alert-rules'],
    queryFn: () =>
      apiClient.get<{ rules: RuleDto[] }>('/admin/alert-rules').then((r) => r.data.rules),
  });

  const remove = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) =>
      apiClient.delete(`/admin/notification-policies/${id}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['notification-policies'] });
      setDeleteErrorKey(null);
      setDeleteTarget(null);
      toast({
        variant: 'info',
        title: t('alerts.toast.policy.deleted'),
        text: variables.name,
        closeLabel: t('common.close'),
      });
    },
    onError: (e: unknown) =>
      setDeleteErrorKey(apiErrorKey(e, 'alerts.policies.error.delete_failed')),
  });

  const rows = policies.data ?? [];
  const channelsList = channels.data ?? [];
  const usedByRules = (policyId: string): number =>
    (rules.data ?? []).filter((r) => r.policyId === policyId).length;
  // A channel the policy points at but that no longer exists still has to read as
  // something: the uuid is what is stored, and saying so beats an empty bullet.
  const channelLabel = (channelId: string): string => {
    const channel = channelsList.find((c) => c.id === channelId);
    return channel ? channel.displayName || channel.name : channelId;
  };

  function editorHref(policyId: string): string {
    return `/system-admin/alert-policies/${policyId}/edit`;
  }

  // The wireframe's `policyMatcherMetadata`: a matcher reads as the fact it
  // selects on, "Severitate: critică" or "Etichetă tip: lipsa-raportare", not as
  // the Alertmanager expression it is stored as.
  function matcherItem(m: PolicyDto['matchers'][number]): { label: string; value: string } {
    const values = Array.isArray(m.value) ? m.value : m.value.split('|');
    if (m.label === 'severity') {
      return {
        label: t('alerts.column.severity'),
        value: values
          .map((v) => (SEVERITIES.includes(v) ? t(`alerts.severity.${v}`).toLocaleLowerCase() : v))
          .join(', '),
      };
    }
    return { label: t('alerts.policies.meta.label', { key: m.label }), value: values.join(', ') };
  }

  // Policy and Channels only (docs/ui-shape-admin-aggregations-alerts.md §4.2,
  // §12): grouping, deduplication and frequency stay in the editor.
  const columns: TableColumn[] = [
    { key: 'actions', label: '', width: '76px' },
    { key: 'policy', label: t('alerts.policies.column.policy') },
    { key: 'channels', label: t('alerts.policies.column.channels'), width: '26%' },
  ];
  const tableRows: TableRowData[] = rows.map((p) => ({ id: p.id }));

  // An empty register keeps its table (AdminAlerte.tsx:3661). Both QueryState
  // branches render this same node, because mud-table draws its `empty` slot on
  // its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-alerts__register">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          rowStyle="zebra"
          disableSort={true}
          aria-label={t('alerts.section.policies')}
          // The whole row opens the policy's editor, the menu's first entry.
          hoverable
          onMudRowClick={onRowOpen((index) => {
            const p = rows[index];
            if (p) navigate(editorHref(p.id));
          })}
        >
          <EmptyRegisterMessage>{t('alerts.policies.empty')}</EmptyRegisterMessage>
          <span slot="header-cell-actions" className="visually-hidden">
            {t('alerts.column.actions')}
          </span>
          {rows.map((p, index) => {
            const used = usedByRules(p.id);
            return [
              <span
                key={`actions-${p.id}`}
                slot={`cell-actions-${index}`}
                className="simsm-alerts__cell simsm-alerts__cell--actions"
              >
                <RowActionsMenu
                  label={t('alerts.action.row_menu', { name: p.displayName })}
                  actions={[
                    { label: t('alerts.action.edit'), onSelect: () => navigate(editorHref(p.id)) },
                    {
                      label: t('alerts.action.delete'),
                      danger: true,
                      separator: true,
                      disabled: used > 0,
                      reason: t('alerts.policies.in_use'),
                      onSelect: () => {
                        setDeleteErrorKey(null);
                        setDeleteTarget({ id: p.id, name: p.displayName });
                      },
                    },
                  ]}
                />
              </span>,
              <span
                key={`policy-${p.id}`}
                slot={`cell-policy-${index}`}
                className="simsm-alerts__cell-name"
              >
                <RouterLink to={editorHref(p.id)} size="sm" variant="strict" underline="hover">
                  {p.displayName}
                </RouterLink>
                <MetadataLine wrap items={p.matchers.map(matcherItem)} />
              </span>,
              // The wireframe lists the channels by name; a bare count says
              // nothing about where the policy actually delivers.
              <span
                key={`channels-${p.id}`}
                slot={`cell-channels-${index}`}
                className="simsm-alerts__cell"
              >
                {p.channelIds.length === 0 ? (
                  <span className="simsm-alerts__muted-text">
                    {t('alerts.policies.no_channels')}
                  </span>
                ) : (
                  <ul className="simsm-alerts__delivery-list">
                    {p.channelIds.map((channelId) => (
                      <li key={channelId}>{channelLabel(channelId)}</li>
                    ))}
                  </ul>
                )}
              </span>,
            ];
          })}
        </MudTable>
      </div>
      <div className="simsm-alerts__table-footer">
        <span className="simsm-alerts__muted-text">
          {t(rows.length === 1 ? 'alerts.policies.count_one' : 'alerts.policies.count_other', {
            count: rows.length,
          })}
        </span>
      </div>
    </>
  );

  return (
    <section className="simsm-page simsm-alerts">
      <PageHeader
        title={t('alerts.section_title')}
        lede={t('alerts.section_lede')}
        actions={
          <>
            {/* The wireframe carries the definitions menu on every definition tab,
                not only the first: the export/import bundle covers all four object
                types whichever tab it is started from. */}
            <AlertDefinitionsMenu />
            <MudButton
              shape="circular"
              variant="primary"
              size="md"
              onClick={() => navigate('/system-admin/alert-policies/new')}
            >
              <MudIcon slot="icon-start" name="plus-large" size={16} />
              {t('alerts.policies.action.new')}
            </MudButton>
          </>
        }
      />

      <AlertsSectionNav />

      <QueryState query={policies} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>

      <MudModal
        closeLabel={t('common.close')}
        open={deleteTarget !== null}
        size="sm"
        titleText={t('alerts.confirm.delete.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — applied to
        // every modal uniformly; a no-op here until a descendant emits its own mudClose.
        onMudClose={onModalDismiss(() => {
          setDeleteTarget(null);
          setDeleteErrorKey(null);
        })}
      >
        {deleteErrorKey ? (
          <MudInfoBox emphasis="strong" role="alert" variant="error">
            {t(deleteErrorKey)}
          </MudInfoBox>
        ) : null}
        {deleteTarget ? (
          <p className="simsm-alerts__confirm-body">
            {t('alerts.confirm.delete.body', { name: deleteTarget.name })}
          </p>
        ) : null}
        <div slot="actions" className="simsm-alerts__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => {
              setDeleteTarget(null);
              setDeleteErrorKey(null);
            }}
          >
            {t('alerts.confirm.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="destructive"
            size="md"
            disabled={remove.isPending}
            onClick={() => {
              if (deleteTarget) {
                setDeleteErrorKey(null);
                remove.mutate({ id: deleteTarget.id, name: deleteTarget.name });
              }
            }}
          >
            {t('alerts.confirm.action.confirm')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
